import React, { createContext, useContext, useEffect, useState } from 'react';
import api from "../api/axios";

interface User {
    id: number;
    username: string;
    nickname: string;
    role: string;
    avatarUrl: string;
    bannerUrl: string;
    aboutMe: string;
    createdAt: string;
}

type AuthContextType = {
    token: string | null;
    role: string | null;
    user: any | null;
    login: (token: string, rememberMe?: boolean) => void;
    logout: () => void;
    isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType>({
    token: null,
    role: null,
    user: null,
    login: () => {},
    logout: () => {},
    isAuthenticated: false,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [token, setToken] = useState<string | null>(() => {
        const sessionToken = sessionStorage.getItem('token');
        if (sessionToken) return sessionToken;
        return localStorage.getItem('token');
    });

    const [role, setRole] = useState<string | null>(() => {
        const sessionRole = sessionStorage.getItem('role');
        if (sessionRole) return sessionRole;
        return localStorage.getItem('role');
    });

    const [user, setUser] = useState<any | null>(null);
    const isAuthenticated = !!token;

    // Функция для загрузки данных пользователя
    const loadUserData = async () => {
        try {
            const userRes = await api.get('/user/profile');
            const userData = userRes.data;
            setUser(userData);
            setRole(userData.role || 'USER');

            const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');
            if (currentToken === sessionStorage.getItem('token')) {
                sessionStorage.setItem('role', userData.role || 'USER');
            } else {
                localStorage.setItem('role', userData.role || 'USER');
            }
        } catch (error) {
            console.error('Не удалось загрузить данные пользователя:', error);
        }
    };

    // При монтировании загружаем данные пользователя если есть токен
    useEffect(() => {
        if (token) {
            loadUserData();
        }
    }, [token]);

    const login = async (newToken: string, rememberMe: boolean = false) => {
        if (rememberMe) {
            localStorage.setItem('token', newToken);
            sessionStorage.removeItem('token');
        } else {
            sessionStorage.setItem('token', newToken);
            localStorage.removeItem('token');
        }

        setToken(newToken);

        try {
            await loadUserData();
        } catch (error) {
            console.error('Ошибка при входе:', error);
            setRole('USER');
            if (rememberMe) {
                localStorage.setItem('role', 'USER');
            } else {
                sessionStorage.setItem('role', 'USER');
            }
        }
    };

    const logout = () => {
        setToken(null);
        setRole(null);
        setUser(null);
        
        // ⬇⬇⬇ ВАЖНО: Удаляем заголовок при выходе! ⬇⬇⬇
        delete api.defaults.headers.common['Authorization'];
        
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('role');
        window.location.href = "/";
    };

    return (
        <AuthContext.Provider value={{ token, role, user, login, logout, isAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);